<template>
  <div
    class="editor-wrapper"
    :style="{
      width: `${width}px`,
      height: `${height}px`
    }"
  >
    <div class="drag-toolbar">
      <v-select
        class="mr-3"
        v-model="dragOption.fontFamily"
        :options="dragToolbarOptions.fontFamilyList"
        :dir="$vs.rtl ? 'rtl' : 'ltr'"
        @input="changeFontFamily"
      />
      <v-select
        class="mr-3"
        v-model="dragOption.fontSize"
        :options="dragToolbarOptions.fontSizeList"
        :dir="$vs.rtl ? 'rtl' : 'ltr'"
        @input="changeFontSize"
      />
      <color-picker v-model="dragOption.color" @input="changeColor" />
      <i class="pointer-icons material-icons ml-3" @click="toggleUnderline">
        format_underline
      </i>
      <i class="pointer-icons material-icons ml-3" @click="toggleBold">
        format_bold
      </i>
      <i class="pointer-icons material-icons ml-3" @click="handleAlignCenter">
        format_align_center
      </i>
      <i class="pointer-icons material-icons ml-auto" @click="togglePhoto">
        insert_photo
      </i>
    </div>
    <img
      class="editor-bg"
      :src="image && image.file && image.file.file_path"
      alt="certificate-bg"
    />
    <Draggable
      v-for="(item, index) in draggableContent"
      :key="index + item.content"
      :data="item"
      :class="{
        activeDragItem: activeDragIndex == index
      }"
      @endDrag="endDrag"
      @onDragMove="(val) => onDragMove(item, val)"
    >
      <div
        class="flex"
        :style="{
          ...item.style,
          left: 'unset',
          top: 'unset',
          transform: 'unset'
        }"
        @mousedown="dragItem(index)"
        :ref="'dragItem' + index"
      >
        <input
          v-if="activeIndex == index && item.type == 'static-text'"
          class="inputx input-text"
          placeholder="text"
          :value="content[activeIndex].content"
          @input="changeContent"
          @keydown="(e) => handleKeyDownInput(e)"
        />
        <div v-else class="drag-item">
          <p @dblclick="setActiveIndex(index)" v-html="item.content"></p>
        </div>
      </div>
    </Draggable>
    <Resizable
      v-for="(item, index) in resizableContent"
      :key="index"
      :data="item"
      :class="{
        activeDragItem: activeResizeIndex == index
      }"
      @endDrag="endResize"
      @onDragMove="(val) => onDragMove(item, val)"
      @onResize="(val) => onResize(item, val)"
    >
      <div
        class="flex"
        :style="item.style"
        @mousedown="resizeItem(index)"
      ></div>
    </Resizable>
    <upload-image-modal
      ref="uploadImageModal"
      @click="activePrompt = true"
      @handleOk="setEditorImage"
    />
  </div>
</template>

<script>
import vSelect from 'vue-select'

import ColorPicker from './ColorPicker.vue'
import QuillEditor from './QuillEditor'
import Draggable from './Draggable'
import Resizable from './Resizable'
import UploadImageModal from '../Modal/UploadImageModal.vue'

import fontData from './fontData'

export default {
  props: {
    template: {
      required: true
    },
    image: {
      type: Object,
      required: true
    },
    dynamicTextList: {
      type: Array,
      required: false
    },
    staticTextList: {
      type: Array
    },
    width: {
      type: Number,
      required: true
    },
    height: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      editorOption: {
        modules: {
          toolbar: '#toolbar'
        }
      },
      content: [],
      inputData: "",
      staticTextContent: [],
      activeIndex: -1,
      mainContent: '',
      dragOption: {
        fontFamily: { id: 1, label: 'Sora', value: 'Sora' },
        fontSize: { id: 2, label: 'Normal', value: '18px' },
        color: '#000000'
      },
      dragToolbarOptions: {
        fontFamilyList: fontData,
        fontSizeList: [
          { id: 1, label: 'Small', value: '12px' }, // 12px
          { id: 2, label: 'Normal', value: '18px' }, // 18px
          { id: 3, label: 'Large', value: '24px' }, // 24px
          { id: 4, label: 'Huge', value: '30px' } // 30px
        ]
      },
      activeDragIndex: -1,
      activeResizeIndex: -1
    }
  },
  components: {
    QuillEditor,
    Draggable,
    vSelect,
    ColorPicker,
    UploadImageModal,
    Resizable
  },
  computed: {
    draggableContent () {
      return this.content.filter((item) => item.type != 'image')
    },
    resizableContent () {
      return this.content.filter((item) => item.type == 'image')
    }
  },
  methods: {
    setActiveIndex (index) {
      this.activeIndex = index
      this.inputData = this.content[this.activeIndex].content
    },
    handleKeyDownInput (e) {
      if (e.keyCode === 13) {
        this.content[this.activeIndex].content = this.inputData
        this.activeIndex = -1
      }
    },
    changeContent (e) {
      this.inputData = e.target.value
    },
    addDynamicText (id) {
      const sameTextCount = this.getSameDynamicTextCount(id)
      const content = this.dynamicTextList.filter(
        (text) => text.field_id == id
      )[0]['field_code']

      if (content == '{staff_name}' && sameTextCount == 5)
        return this.$vs.notify({
          title: 'Error',
          text: 'You can select a maximum of 5 Staff Name',
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle'
        })

      if (content == '{student_name}' && sameTextCount == 1)
        return this.$vs.notify({
          title: 'Error',
          text: 'You can select only one Student Name',
          color: 'danger',
          iconPack: 'feather',
          icon: 'icon-alert-circle'
        })

      this.content.push({
        id: id,
        type: 'dynamic-text',
        style: {
          fontSize: '18px',
          width: 'max-content',
          fontFamily: 'Sora'
        },
        content: content + (sameTextCount ? sameTextCount : ''),
        x: 0,
        y: 0
      })
    },
    getSameDynamicTextCount (id) {
      let count = 0

      this.content.map((item) => {
        if (item.id == id) count++
      })
      return count
    },
    addStaticText (text) {
      this.content.push({
        type: 'static-text',
        style: {
          fontSize: '18px',
          width: 'max-content',
          fontFamily: 'Sora'
        },
        content: text,
        x: 300,
        y: 300
      })
    },
    removeStaticText (text) {
      const index = this.content.findIndex((item) => item.content == text)
      this.content.splice(index, 1)
    },
    dragItem (index) {
      this.activeDragIndex = index
      this.dragOption.fontSize = this.getFontSizeOption(
        this.draggableContent[index].style.fontSize
      )
      this.dragOption.fontFamily = this.getFontFamilyOption(
        this.draggableContent[index].style.fontFamily
      )
      this.dragOption.color = this.draggableContent[index].style.color || '#000000'
    },
    resizeItem (index) {
      this.activeResizeIndex = index
    },
    endDrag () {
      this.activeDragIndex = -1
    },
    endResize () {
      this.activeResizeIndex = -1
    },
    getFontSizeOption (size) {
      switch (size) {
        case '12px':
          return { id: 1, label: 'Small', value: '12px' }
        case '18px':
          return { id: 2, label: 'Normal', value: '18px' }
        case '24px':
          return { id: 3, label: 'Large', value: '24px' }
        case '30px':
          return { id: 4, label: 'Huge', value: '30px' }
        default:
          return { id: 2, label: 'Normal', value: '18px' }
      }
    },
    getFontFamilyOption (family) {
      let fontOption = {}
      this.dragToolbarOptions.fontFamilyList.map((option) => {
        if (option.value === family) {
          fontOption = option
        }
      })
      return fontOption
    },
    getContentIndexFromDragIndex (dragIndex) {
      const content = this.draggableContent[dragIndex]
      const contentIndex = this.content.findIndex((item) => item.type == content.type && item.content == content.content)

      return contentIndex
    },
    changeFontSize () {
      if (this.activeDragIndex != -1) {
        this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style.fontSize =
          this.dragOption.fontSize.value
      }
    },
    changeFontFamily () {
      if (this.activeDragIndex != -1) {
        this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style.fontFamily =
          this.dragOption.fontFamily.value
      }
    },
    changeColor () {
      if (this.activeDragIndex != -1) {
        this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style.color = this.dragOption.color
      }
    },
    toggleUnderline () {
      if (this.activeDragIndex != -1) {
        const value = this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style.textDecoration
        this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style = {
          ...this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style,
          textDecoration: value && value == 'underline' ? 'unset' : 'underline'
        }
      }
    },
    toggleBold () {
      if (this.activeDragIndex != -1) {
        const value = this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style.fontWeight
        this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style = {
          ...this.content[this.getContentIndexFromDragIndex(this.activeDragIndex)].style,
          fontWeight: value && value == 'bold' ? 'normal' : 'bold'
        }
      }
    },
    handleAlignCenter () {
      if (this.activeDragIndex != -1) {
        const elementWidth =
          this.$refs[`dragItem${this.activeDragIndex}`][0].offsetWidth
        const contentIndex = this.getContentIndexFromDragIndex(this.activeDragIndex)

        this.content = [
          ...this.content.map((item, index) =>
            index === contentIndex
              ? {
                ...item,
                x: this.width / 2 - elementWidth / 2,
                style: {
                  ...item.style,
                  left: Math.floor(this.width / 2) + "px",
                  transform: 'translateX(-50%)'
                }
              }
              : item
          )
        ]
      }
    },
    onDragMove (item, { x, y }) {
      const index = this.content.findIndex(
        (data) =>
          data.type == item.type &&
          data.style.backgroundImage == item.style.backgroundImage &&
          data.content == item.content
      )
      this.content[index].x = Math.floor(x)
      this.content[index].y = Math.floor(y)
      this.content[index].style.left = Math.floor(x) + 'px'
      this.content[index].style.top = Math.floor(y) + 'px'
      this.content[index].style.transform = 'unset'
    },
    onResize (item, { width, height }) {
      const index = this.content.findIndex(
        (data) =>
          data.type == item.type &&
          data.style.backgroundImage == item.style.backgroundImage
      )
      this.content[index].style.width = Math.floor(width) + 'px'
      this.content[index].style.height = Math.floor(height) + 'px'
    },
    setContent (content) {
      this.content = content
    },
    togglePhoto () {
      this.$refs.uploadImageModal.toggleModal()
    },
    setEditorImage (image) {
      this.content.push({
        type: 'image',
        style: {
          fontSize: '18px',
          width: '50px',
          height: '50px',
          backgroundImage: `url(${image})`,
          backgroundSize: 'cover'
        },
        content: '',
        x: 0,
        y: 0
      })
    }
  },
  watch: {
    mainContent (newVal) {
      this.$emit('changeMainContent', newVal)
    }
  },
  mounted () {
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Delete') {
        if (this.activeDragIndex == -1) return

        const textInfo = this.content[this.activeDragIndex]

        if (textInfo.type == 'static-text') {
          this.$emit('removeStaticText', textInfo.content)
        } else {
          this.content = this.content.filter((item) => item.id != textInfo.id)
        }
      }
    })
  }
}
</script>

<style>
.editor {
  height: 925px;
}

.editor-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  margin-right: auto;
  margin-left: auto;
}

.editor-bg {
  position: absolute;
  width: 100%;
  height: 100%;
}

.template-content {
  align-items: center;
}

.certificate-editor {
  width: 840px !important;
  height: 547px !important;
  background-color: white !important;
  margin-top: 10;
  margin-left: 10;
}

.editor-static-text {
  position: absolute;
  top: 12px;
  left: 15px;
}

.vs-switch {
  position: absolute;
  top: -125px;
  left: 0;
}

.drag-toolbar {
  position: absolute;
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: white;
  width: 100%;
  top: -85px;
  left: 0px;
}

.mr-3 .vs__dropdown-toggle {
  min-width: 140px !important;
  max-width: 240px !important;
}

.activeDragItem {
  border: 1px solid rgb(66, 66, 150) !important;
  border-radius: 4px;
}

.resizable.activeDragItem {
  padding: 0;
}

.draggable .drag-item {
  position: relative;
}

.draggable .drag-item .material-icons {
  position: absolute;
  top: -35px;
  left: 95%;
}

.input-text {
  border: none;
  background: center;
}

.pointer-icons,
.vs__actions .vs__open-indicator {
  cursor: pointer;
}
</style>
